import { Avatar, type AvatarProps } from './Avatar';
import { Breadcrumbs } from './Breadcrumbs';
import { Button, type ButtonSize, type ButtonVariant } from './Button';
import { Checkbox, type CheckboxProps } from './Checkbox';
import { ChevronButton } from './ChevronButton/ChevronButton';
import { ChevronIcon } from './ChevronIcon/ChevronIcon';
import { ColumnLayout } from './ColumnLayout';
import { DatePicker, type DatePickerProps } from './DatePicker';
import { ErrorPage, type ErrorPageProps } from './ErrorPage';
import { FieldMessage } from './FieldMessage';
import { Form, type FormProps, useFieldIsRequired } from './Form';
import type { FormFieldInputProps, FormFieldProps } from './FormField';
import {
  FormField,
  FormFieldCurrencyInput,
  FormFieldDropdown,
  FormFieldDropdownMultiSelect,
  FormFieldDropdownMultiSelectAsync,
  FormFieldDropdownSingleSelect,
  FormFieldDropdownSingleSelectAsync,
  FormFieldDropdownSortableMultiSelectAsync,
  FormFieldDropdownTreeSingleSelect,
  FormFieldInput,
  FormFieldRadioGroup,
  FormFieldTextArea,
  FormFieldToggle,
} from './FormField';
import { Icon, type IconName, type IconSize, type IconVariant } from './Icon';
import { IconButton } from './IconButton';
import {
  type BaseInputProps,
  type CurrencyInputProps,
  Input,
  type InputTextProps,
  type TextAreaProps,
} from './Input';
import { TextArea as TextAreaComponent } from './Input/components/TextArea';
import { KebabMenu, type MenuItemState } from './KebabMenu';
import { Label } from './Label';
import { ModalDialog, type ModalDialogRef } from './ModalDialog';
import { Popover } from './Popover';
import { RadioGroup, type RadioGroupProps } from './RadioGroup';
import { Scrollbars } from './Scrollbars';
import {
  SegmentedControl,
  type SegmentedControlProps,
} from './SegmentedControl';
import {
  type DropdownSingleSelectAsyncProps,
  type DropdownSingleSelectProps,
  type DropdownTreeSingleSelectProps,
  type MultiSelectProps,
  type RenderDropdownProps,
  Select,
  type SortableMultiSelectProps,
  type SortableMultiSelectSecondaryAction,
} from './Select';
import { Skeleton } from './Skeleton';
import {
  SlideOut,
  type SlideOutProps,
  type SlideOutRef,
  type SlideOutStatus,
} from './SlideOut';
import { Spinner } from './Spinner';
import { Table } from './Table';
import { Tag } from './Tag';
import { showToast, Toast } from './Toast';
import { Toggle, type ToggleProps } from './Toggle';
import {
  Tooltip,
  type TooltipBehavior,
  type TooltipContentLayout,
  type TooltipLevel,
  type TooltipOffset,
  type TooltipProps,
  type TooltipSize,
  type TooltipSpacing,
  type TooltipTone,
} from './Tooltip';

/**
 * @deprecated Use `<Input.Currency />`.
 */
const CurrencyInput = Input.Currency;

/**
 * @deprecated Use `<Input.Text type="textarea" />`.
 */
const TextArea = TextAreaComponent;

/**
 * @deprecated Use `<Select.Single />`.
 */
const DropdownSingleSelect = Select.Single;

/**
 * @deprecated Use `<Select.SingleAsync />`.
 */
const DropdownSingleSelectAsync = Select.SingleAsync;

/**
 * @deprecated Use `<Select.SingleTree />`.
 */
const DropdownTreeSingleSelect = Select.SingleTree;

/**
 * @deprecated Use `<Select.Multi />`.
 */
const MultiSelect = Select.Multi;

/**
 * @deprecated Use `<Select.MultiSortable />`.
 */
const SortableMultiSelect = Select.MultiSortable;

// eslint-disable-next-line import/no-default-export
export default {
  Avatar,
  Breadcrumbs,
  Button,
  Checkbox,
  ChevronButton,
  ChevronIcon,
  ColumnLayout,
  // Public deprecated export. Federated apps still import this name.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  CurrencyInput,
  DatePicker,
  // Public deprecated export. Federated apps still import this name.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  DropdownSingleSelect,
  // Public deprecated export. Federated apps still import this name.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  DropdownSingleSelectAsync,
  // Public deprecated export. Federated apps still import this name.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  DropdownTreeSingleSelect,
  ErrorPage,
  FieldMessage,
  Form,
  FormField,
  FormFieldInput,
  FormFieldDropdown,
  FormFieldDropdownMultiSelect,
  FormFieldDropdownMultiSelectAsync,
  FormFieldDropdownSortableMultiSelectAsync,
  FormFieldDropdownSingleSelect,
  FormFieldDropdownSingleSelectAsync,
  FormFieldDropdownTreeSingleSelect,
  FormFieldCurrencyInput,
  FormFieldRadioGroup,
  FormFieldTextArea,
  FormFieldToggle,
  Icon,
  IconButton,
  Input,
  KebabMenu,
  Label,
  ModalDialog,
  // Public deprecated export. Federated apps still import this name.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  MultiSelect,
  Popover,
  RadioGroup,
  // Public deprecated export. Federated apps still import this name.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  SortableMultiSelect,
  showToast,
  SlideOut,
  Spinner,
  Skeleton,
  Table,
  Tag,
  // Public deprecated export. Federated apps still import this name.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  TextArea,
  Toggle,
  Toast,
  Tooltip,
  useFieldIsRequired,
  Scrollbars,
  SegmentedControl,
  Select,
};

export {
  Avatar,
  type AvatarProps,
  type BaseInputProps,
  Breadcrumbs,
  Button,
  type ButtonSize,
  type ButtonVariant,
  Checkbox,
  type CheckboxProps,
  ChevronButton,
  ChevronIcon,
  ColumnLayout,
  CurrencyInput,
  type CurrencyInputProps,
  DatePicker,
  type DatePickerProps,
  DropdownSingleSelect,
  DropdownSingleSelectAsync,
  type DropdownSingleSelectAsyncProps,
  type DropdownSingleSelectProps,
  DropdownTreeSingleSelect,
  type DropdownTreeSingleSelectProps,
  ErrorPage,
  type ErrorPageProps,
  FieldMessage,
  Form,
  FormField,
  FormFieldCurrencyInput,
  FormFieldDropdown,
  FormFieldDropdownMultiSelect,
  FormFieldDropdownMultiSelectAsync,
  FormFieldDropdownSingleSelect,
  FormFieldDropdownSingleSelectAsync,
  FormFieldDropdownSortableMultiSelectAsync,
  FormFieldDropdownTreeSingleSelect,
  FormFieldInput,
  type FormFieldInputProps,
  type FormFieldProps,
  FormFieldRadioGroup,
  FormFieldTextArea,
  FormFieldToggle,
  type FormProps,
  Icon,
  IconButton,
  type IconName,
  type IconSize,
  type IconVariant,
  Input,
  type InputTextProps,
  KebabMenu,
  Label,
  type MenuItemState,
  ModalDialog,
  type ModalDialogRef,
  MultiSelect,
  type MultiSelectProps,
  Popover,
  RadioGroup,
  type RadioGroupProps,
  type RenderDropdownProps,
  Scrollbars,
  SegmentedControl,
  type SegmentedControlProps,
  Select,
  showToast,
  Skeleton,
  SlideOut,
  type SlideOutProps,
  type SlideOutRef,
  type SlideOutStatus,
  SortableMultiSelect,
  type SortableMultiSelectProps,
  type SortableMultiSelectSecondaryAction,
  Spinner,
  Table,
  Tag,
  TextArea,
  type TextAreaProps,
  Toast,
  Toggle,
  type ToggleProps,
  Tooltip,
  type TooltipBehavior,
  type TooltipContentLayout,
  type TooltipLevel,
  type TooltipOffset,
  type TooltipProps,
  type TooltipSize,
  type TooltipSpacing,
  type TooltipTone,
  useFieldIsRequired,
};
